import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiDotsHorizontalRounded } from 'react-icons/bi'
import { HiOutlinePencil, HiOutlineTrash, HiOutlineUserGroup, HiOutlineLockClosed } from 'react-icons/hi'
import { useClickOutside } from '../../../hooks/useClickOutside'

/**
 * ⋯ menu on a template card for the people who may change the saved original
 * (its author and school admins): edit, share / stop sharing, delete.
 */
function TemplateActionsMenu({ templateId, shared, onEdit, onToggleShare, onDelete }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)
    const triggerRef = useRef(null)
    const itemRefs = useRef([])

    const close = useCallback(() => setOpen(false), [])
    useClickOutside(wrapRef, close, open)

    // Focus the first item on open so the menu is usable from the keyboard.
    useEffect(() => {
        if (open) itemRefs.current[0]?.focus()
    }, [open])

    const items = [
        { key: 'edit', label: t('editTemplate'), Icon: HiOutlinePencil, action: onEdit },
        shared
            ? { key: 'unshare', label: t('stopSharingTemplate'), Icon: HiOutlineLockClosed, action: onToggleShare }
            : { key: 'share', label: t('shareTemplateWithSchool'), Icon: HiOutlineUserGroup, action: onToggleShare },
        { key: 'delete', label: t('deleteTemplate'), Icon: HiOutlineTrash, action: onDelete, danger: true },
    ]

    const run = (action) => () => {
        setOpen(false)
        triggerRef.current?.focus()
        action()
    }

    const onMenuKeyDown = (event) => {
        const nodes = itemRefs.current.filter(Boolean)
        const index = nodes.indexOf(document.activeElement)
        const step = { ArrowDown: 1, ArrowUp: -1 }[event.key]
        if (step) {
            event.preventDefault()
            nodes[(index + step + nodes.length) % nodes.length]?.focus()
        } else if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault()
            nodes[event.key === 'Home' ? 0 : nodes.length - 1]?.focus()
        } else if (event.key === 'Tab' || event.key === 'Escape') {
            setOpen(false)
            if (event.key === 'Escape') triggerRef.current?.focus()
        }
    }

    const menuId = `template-actions-${templateId}`

    return (
        <div className="relative flex-shrink-0" ref={wrapRef}>
            <button
                ref={triggerRef}
                type="button"
                className="coc-kebab"
                id={`template-actions-button-${templateId}`}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={t('templateActions')}
                onClick={() => setOpen((prev) => !prev)}
            >
                <BiDotsHorizontalRounded size={22} aria-hidden="true" />
            </button>
            {open && (
                <div className="coc-menu" role="menu" id={menuId} aria-labelledby={`template-actions-button-${templateId}`} onKeyDown={onMenuKeyDown}>
                    {items.map(({ key, label, Icon, action, danger }, i) => (
                        <button
                            key={key}
                            ref={(node) => (itemRefs.current[i] = node)}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            id={`template-action-${key}-${templateId}`}
                            className={`coc-menu-item${danger ? ' is-danger' : ''}`}
                            onClick={run(action)}
                        >
                            <Icon size={18} aria-hidden="true" />
                            <span>{label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

export default TemplateActionsMenu
